import React, { useEffect, useRef, useState } from "react";
import JoditEditor from "jodit-react";
import useApi from "../hook/useApi";
import { toast } from "react-toastify";
import Loader from "./Loader/Loader";
import { CMS_WEBSITE_TYPE, PAGE_TYPE, PLATFORM_TYPE } from "../utils/enum";
import { stripHtml } from "../utils/helper";

const PrivacyPolicyLayer = () => {
  const editor = useRef(null);
  const { AsyncGetApiCall, AsyncPatchAPICall } = useApi();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ title: "", content: "" });
  const [platformType, setPlatformType] = useState(PLATFORM_TYPE.ICO);

  const getData = async () => {
    setLoading(true);
    const url = "/cmspage/get-page";

    const query = {
      pageType: PAGE_TYPE.PRIVACY_POLICY,
      webType:
        platformType === PLATFORM_TYPE.ICO
          ? CMS_WEBSITE_TYPE.ICO
          : CMS_WEBSITE_TYPE.TRAVEL,
    };

    const result = await AsyncGetApiCall(url, query);
    if (result.success) {
      setData(result.data);
    } else {
      toast.error(result.errors[0]);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, [platformType]);

  const handleContentChange = (content) => {
    setData((prev) => ({ ...prev, content }));
    // Validate content on change
    const plainTextContent = stripHtml(content).trim();
    setErrors((prev) => ({
      ...prev,
      content: plainTextContent ? "" : "Content is required",
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const plainTextContent = stripHtml(data.content).trim();
    if (!plainTextContent) {
      newErrors.content = "Content is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    const result = await AsyncPatchAPICall(
      `/cmspage/update-page/${data?._id}`,
      {
        title: data?.title,
        content: data.content,
      }
    );
    if (result.success) {
      setData(result.data);
      toast.success("Content updated successfully");
    } else {
      toast.error(result.errors[0]);
    }
    setLoading(false);
  };

  const handlePlatformChange = (e) => {
    const selected = e.target.value;
    setPlatformType(selected);
  };

  const editorConfig = {
    placeholder: stripHtml(data.content).trim() ? "" : "Start typing...",
    readonly: false,
    allowClipboardAccess: true,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    processPasteHTML: true,
    defaultActionOnPaste: "insert_as_html", // allows rich content (images, formatting)
    // If you want to sanitize pasted content:
    pasteHTMLActionList: [
      { value: "insert_as_html", text: "Insert as HTML" },
      { value: "insert_as_text", text: "Insert as text" },
      { value: "insert_only_text", text: "Insert only text" },
    ],
    // Optional: clean pasted HTML
    cleanHTML: {
      removeEmptyElements: true,
      fillEmptyParagraph: false,
      replaceNBSP: true,
    },
  };

  return (
    <>
      {loading && <Loader />}
      <div className="mb-3">
        <select
          className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
          defaultValue="Select Status"
          value={platformType}
          onChange={handlePlatformChange}
        >
          <option value="Select Status" disabled>
            Select Platform
          </option>
          <option value={PLATFORM_TYPE.ICO}>ICO</option>
          <option value={PLATFORM_TYPE.TRAVEL}>TRAVEL</option>
        </select>
      </div>
      <div className="card basic-data-table radius-12 overflow-hidden">
        <div className="card-body p-0">
          {/* Jodit Editor */}
          <div className={errors.content ? "is-invalid" : ""}>
            <JoditEditor
              ref={editor}
              value={data.content}
              config={editorConfig}
              onBlur={handleContentChange}
            />
            {errors.content && (
              <div className="invalid-feedback">{errors.content}</div>
            )}
          </div>
        </div>
        <div className="card-footer p-24 bg-base border border-bottom-0 border-end-0 border-start-0">
          <div className="d-flex align-items-center justify-content-center gap-3">
            <button
              type="button"
              className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-50 py-11 radius-8"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary border border-primary-600 text-md px-28 py-12 radius-8"
              onClick={handleSave}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyLayer;
